import React, { useState } from "react";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiMessageSquare,
  FiSend,
} from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { FaWhatsapp } from "react-icons/fa";
import { MdPhone, MdEmail } from "react-icons/md";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createPopupInquiry } from "../../api/api";

const InquiryModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    inquiryTopic: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    // Last name is now optional - removed required validation
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Email is invalid";
    if (!formData.mobile.trim()) newErrors.mobile = "Mobile number is required";
    else if (!/^\d{10}$/.test(formData.mobile)) newErrors.mobile = "Mobile number must be 10 digits";
    if (!formData.inquiryTopic) newErrors.inquiryTopic = "Please select a topic";
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Prepare data for API
    const inquiryData = {
      name: formData.firstName + (formData.lastName ? ` ${formData.lastName}` : ''),
      phone: formData.mobile,
      email: formData.email,
      age: "", // Age field - you can add this to your form if needed
      subject: formData.inquiryTopic, // Inquiry topic maps to subject
      notes: formData.message // Message maps to notes
    };

    try {
      setIsSubmitting(true);
      const response = await createPopupInquiry(inquiryData);
      
      if (response && response.status) {
        toast.success('Inquiry submitted successfully! We will get back to you soon.');
        // Reset form after successful submission
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          mobile: "",
          inquiryTopic: "",
          message: "",
        });
        // Close modal after short delay
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        toast.error(response?.message || 'Failed to submit inquiry. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error(error?.response?.data?.message || 'Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Inquiry topic options - mapped to subject field
  const inquiryTopics = [
    { value: "", label: "Select Inquiry Topic" },
    { value: "General Inquiry", label: "General Inquiry" },
    { value: "Course Information", label: "Course Information" },
    { value: "Admissions & Enrollment", label: "Admissions & Enrollment" },
    { value: "Certification Programs", label: "Certification Programs" },
    { value: "Workshops & Webinars", label: "Workshops & Webinars" },
    { value: "Student Support", label: "Student Support" },
    { value: "Technical Support", label: "Technical Support" },
    { value: "Payment & Billing", label: "Payment & Billing" },
    { value: "Partnership & Collaboration", label: "Partnership & Collaboration" },
    { value: "Career Opportunities", label: "Career Opportunities" },
    { value: "Feedback & Suggestions", label: "Feedback & Suggestions" },
    { value: "Other", label: "Other" },
  ];

  // Handle call, WhatsApp, and email actions
  const handleCall = () => {
    window.location.href = "tel:+919557439399";
  };

  const handleWhatsApp = () => {
    window.open("https://wa.me/+919557439399", "_blank");
  };

  const handleEmail = () => {
    window.location.href = "mailto:info@nutrillion.one";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-3">
      <div className="bg-white p-5 rounded-md w-full max-w-[650px] overflow-auto max-h-screen relative">
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        >
          <IoClose size={24} />
        </button>

        {/* Heading */}
        <h2 className="custom-text-space text-lg md:pt-0 pt-4 sm:text-xl font-bold font-lato text-gray-800 text-center md:text-left font-merriweather">
          How can we help you?
        </h2>

        <p className="custom-text-space text-gray-500 text-center md:text-left font-lato">
          Fill in a few details and our team will get back to you within 24 hours.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">

          {/* First + Last Name */}
          <div className="flex flex-col sm:flex-row sm:space-x-4">
            <div className="w-full sm:w-1/2">
<label htmlFor="firstName" className="text-gray-700 text-sm font-lato font-bold">
                First Name <span className="text-danger">*</span>
              </label>
              <div className="flex h-11 items-center rounded-md border border-gray-300 px-3 mt-1 focus-within:border-[#1E88E5]">
                <FiUser className="mr-2 text-gray-400 flex-shrink-0" />
                <input
                  id="firstName"
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="First name"
                  className="w-full text-sm outline-none font-lato"
                />
              </div>
              {errors.firstName && <p className="text-red-500 text-xs mt-1 font-lato">{errors.firstName}</p>}
            </div>

            <div className="w-full sm:w-1/2">
              <label htmlFor="lastName" className="text-gray-700 text-sm font-lato font-bold">
                Last Name <span className="text-gray-400">(optional)</span>
              </label>
              <div className="flex h-11 items-center mt-1 rounded-md border border-gray-300 px-3 focus-within:border-[#1E88E5]">
                <FiUser className="mr-2 text-gray-400 flex-shrink-0" />
                <input
                  id="lastName"
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Last name"
                  className="w-full text-sm outline-none font-lato"
                />
              </div>
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="text-gray-700 text-sm font-lato font-bold">
              Email Address <span className="text-danger">*</span>
            </label>
            <div className="flex h-11 mt-1 items-center rounded-md border border-gray-300 px-3 focus-within:border-[#1E88E5]">
              <FiMail className="mr-2 text-gray-400 flex-shrink-0" />
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email address"
                className="w-full text-sm outline-none font-lato"
              />
            </div>
            {errors.email && <p className="text-red-500 text-xs mt-1 font-lato">{errors.email}</p>}
          </div>

          {/* Mobile + Inquiry Topic */}
          <div className="flex flex-col sm:flex-row sm:space-x-4">
            <div className="w-full sm:w-1/2">
              <label htmlFor="mobile" className="text-gray-700 text-sm font-lato font-bold">
                Mobile Number <span className="text-danger">*</span>
              </label>
              <div className="flex mt-1 h-11 rounded-md border border-gray-300 focus-within:border-[#1E88E5] overflow-hidden">
                <div className="flex w-14 items-center justify-center border-r bg-gray-50 text-sm font-lato flex-shrink-0">
                  +91
                </div>
                <input
                  id="mobile"
                  type="text"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="Phone number"
                  className="w-full px-3 text-sm outline-none font-lato min-w-0"
                />
              </div>
              {errors.mobile && <p className="text-red-500 text-xs mt-1 font-lato">{errors.mobile}</p>}
            </div>

            <div className="w-full sm:w-1/2">
              <label htmlFor="inquiryTopic" className="text-gray-700 font-bold text-sm font-lato">
                Inquiry Topic <span className="text-danger">*</span>
              </label>
              <select
                id="inquiryTopic"
                name="inquiryTopic"
                value={formData.inquiryTopic}
                onChange={handleChange}
                className="w-full h-11 mt-1 rounded-md border border-gray-300 px-3 text-sm outline-none bg-white font-lato focus:border-[#1E88E5]"
              >
                {inquiryTopics.map((topic) => (
                  <option key={topic.value} value={topic.value}>
                    {topic.label}
                  </option>
                ))}
              </select>
              {errors.inquiryTopic && <p className="text-red-500 text-xs mt-1 font-lato">{errors.inquiryTopic}</p>}
            </div>
          </div>

          {/* Message */}
          <div>
            <label htmlFor="message" className="text-gray-700 text-sm font-lato font-bold">
              Message <span className="text-gray-400 ">(optional)</span>
            </label>
            <div className="flex mt-1 rounded-md border border-gray-300 px-3 py-2 focus-within:border-[#1E88E5]">
              <FiMessageSquare className="mt-1 mr-2 text-gray-400 flex-shrink-0" />
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                placeholder="Tell us a little about what you're looking for..."
                className="w-full resize-none text-sm outline-none font-lato"
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full bg-[#22b674] text-white py-2 rounded mt-4 font-roboto transform transition-all duration-300 ease-in-out ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#1a9a5e]'}`}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </>
            ) : (
              <>
                <FiSend className="inline mr-2" />
                Send Inquiry
              </>
            )}
          </button>

          {/* Divider */}
          {/* <div className="relative py-2 text-center text-sm text-gray-400 font-lato">
            <div className="absolute left-0 top-1/2 h-px w-full bg-gray-200"></div>
            <span className="relative bg-white px-3">
              or reach us directly
            </span>
          </div> */}

          {/* Bottom Buttons with React Icons */}
          {/* <div className="grid grid-cols-3 gap-3">
            <button
              type="button"
              onClick={handleCall}
              className="flex h-11 items-center font-bold justify-center gap-2 rounded-md border border-gray-200 text-sm  hover:bg-gray-50 font-lato transition-colors"
            >
              <MdPhone className="text-green-600" size={18} />
              Call Us
            </button>
            <button
              type="button"
              onClick={handleWhatsApp}
              className="flex h-11 items-center justify-center gap-2 rounded-md border border-gray-200 text-sm font-bold hover:bg-gray-50 font-lato transition-colors"
            >
              <FaWhatsapp className="text-green-500" size={18} />
              WhatsApp
            </button>
            <button
              type="button"
              onClick={handleEmail}
              className="flex h-11 items-center justify-center gap-2 rounded-md border border-gray-200 text-sm font-bold hover:bg-gray-50 font-lato transition-colors"
            >
              <MdEmail className="text-green-500" size={18} />
              Email Us
            </button>
          </div> */}

        </form>
      </div>
      <ToastContainer position="top-right" autoClose={5000} />
    </div>
  );
};

export default InquiryModal;